import React, { useState, useEffect } from 'react';
import { X, PhoneCall, CheckCircle, MessageSquare } from 'lucide-react';
import { InteractiveHoverButton } from './magicui/InteractiveHoverButton';
import { trackEvent } from '../lib/analytics';

export function BookingModal({ isOpen, onClose, onOpenPrivacy }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', grade: 'Grade 12', focus: 'Chemistry & STEM' });
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isError, setIsError] = useState(false);

  // Keyboard Escape Key Trap & Focus Management
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) return;

    setIsSubmitting(true);
    setIsError(false);

    trackEvent('strategy_session_submit', formData);

    let success = false;

    // 1. Dispatch through Cloudflare Worker API proxy (/api/telegram) to prevent CORS and token exposure
    try {
      const proxyRes = await fetch('/api/telegram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (proxyRes.ok) {
        const proxyData = await proxyRes.json();
        if (proxyData.success) success = true;
      }
    } catch {
      // Proxy unavailable in local dev, fallback to direct dispatch
    }

    // 2. Direct Fallback Telegram Dispatch if proxy skipped
    if (!success) {
      const telegramToken = import.meta.env.VITE_TELEGRAM_BOT_TOKEN || '8628278384:AAFVMdGpZXV2BZjOpQQCzpm-urq6FHAB4rM';
      const telegramChatIdsRaw = import.meta.env.VITE_TELEGRAM_CHAT_ID || '1024918448,1413655207';
      const chatIds = telegramChatIdsRaw.split(',').map(id => id.trim()).filter(Boolean);

      if (telegramToken && chatIds.length > 0) {
        const text = `🎓 *New GradeLynk Strategy Booking!*\n\n👤 *Name:* ${formData.name}\n📞 *Phone:* \`${formData.phone}\`\n✉️ *Email:* ${formData.email}\n📚 *Grade:* ${formData.grade}\n🎯 *Focus:* ${formData.focus}\n⏰ *Time:* ${new Date().toLocaleString()}`;

        for (const chatId of chatIds) {
          try {
            const res = await fetch(`https://api.telegram.org/bot${telegramToken}/sendMessage`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ chat_id: chatId, text, parse_mode: 'Markdown' }),
            });
            if (res.ok) success = true;
          } catch (err) {
            console.warn(`Telegram fallback dispatch error for ${chatId}:`, err);
          }
        }
      }
    }

    // 3. Backup resilience in localStorage
    const existing = JSON.parse(localStorage.getItem('gradelynk_bookings') || '[]');
    existing.push({ ...formData, timestamp: new Date().toISOString(), dispatched: success });
    localStorage.setItem('gradelynk_bookings', JSON.stringify(existing));

    setIsSubmitting(false);
    setSubmitted(true);
  };

  const smsUri = `sms:+14374382820?body=${encodeURIComponent(
    `Hi Kian, I submitted a strategy request for ${formData.name} (${formData.grade}). Please call or message me!`
  )}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md" role="dialog" aria-modal="true" aria-labelledby="booking-modal-title">
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl p-6 sm:p-8 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4 mb-6">
          <h3 id="booking-modal-title" className="text-xl font-bold text-slate-900 dark:text-white">Book Free 15-Min Strategy Session</h3>
          <button onClick={onClose} aria-label="Close Booking Modal" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white transition cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {submitted ? (
          <div className="py-6 text-center space-y-4">
            <div className="inline-flex p-3 rounded-full bg-emerald-500/10 text-emerald-500">
              <CheckCircle className="h-10 w-10" />
            </div>
            <h4 className="text-2xl font-bold text-slate-900 dark:text-white">Strategy Session Requested!</h4>
            <p className="text-slate-600 dark:text-slate-300 text-sm">
              {isError
                ? 'Your request was saved locally. You can also send Kian a direct SMS message below for an instant response.'
                : 'Kian will call your phone number within 2 hours to confirm your 15-minute diagnostic session.'}
            </p>

            {/* Missed-Call SMS Trigger Fallback */}
            <div className="pt-2 flex flex-col gap-2">
              <a
                href={smsUri}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-amber-400/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 font-bold text-sm hover:bg-amber-500/20 transition"
              >
                <MessageSquare className="h-4 w-4" /> Send Instant SMS to Kian: +1 (437) 438-2820
              </a>
              <button onClick={onClose} className="w-full py-3 rounded-xl bg-amber-500 text-slate-950 font-bold cursor-pointer">
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Student / Parent Name</label>
              <input
                type="text"
                required
                autoFocus
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Sarah Jenkins"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 placeholder-slate-400 focus:border-amber-500 focus:outline-none dark:border-slate-800 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Email Address</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="parent@example.com"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 placeholder-slate-400 focus:border-amber-500 focus:outline-none dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Phone Number</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(416) 555-0199"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 placeholder-slate-400 focus:border-amber-500 focus:outline-none dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Current Grade Level</label>
                <select
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-amber-500 focus:outline-none dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                >
                  <option value="Grade 11">Grade 11</option>
                  <option value="Grade 12">Grade 12</option>
                  <option value="Grade 9-10">Grade 9-10</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Primary Focus Area</label>
                <select
                  value={formData.focus}
                  onChange={(e) => setFormData({ ...formData, focus: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-amber-500 focus:outline-none dark:border-slate-800 dark:bg-slate-950 dark:text-white"
                >
                  <option value="Chemistry & STEM">Chemistry & STEM</option>
                  <option value="Functions & Calculus">Functions & Calculus</option>
                  <option value="Pre-Med & Science Prep">Pre-Med & Science Prep</option>
                  <option value="SAT & English Prep">SAT & English Prep</option>
                </select>
              </div>
            </div>

            {/* Consent Checkbox */}
            <div className="flex items-start gap-2 pt-1">
              <input
                type="checkbox"
                id="consentCheck"
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400 cursor-pointer"
              />
              <label htmlFor="consentCheck" className="text-xs text-slate-600 dark:text-slate-400 leading-normal cursor-pointer">
                I agree to the{' '}
                <button type="button" onClick={onOpenPrivacy} className="underline text-amber-600 dark:text-amber-400 font-semibold hover:text-amber-500">
                  Privacy Policy & SMS Terms
                </button>{' '}
                to receive direct appointment calls/SMS from Kian (+1 437 438-2820).
              </label>
            </div>

            <div className="pt-2 space-y-3">
              <InteractiveHoverButton className="w-full">
                {isSubmitting ? 'Submitting Request...' : 'Confirm Strategy Session Request'}
              </InteractiveHoverButton>

              <div className="text-center">
                <a href="tel:+14374382820" className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 font-semibold hover:text-amber-500">
                  <PhoneCall className="h-3 w-3" /> Or Call Kian Directly: +1 (437) 438-2820
                </a>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
